import { useEffect, useId, useState } from 'react';
import { Button } from '../components/Button/Button';
import { RevealableInput } from '../components/RevealableInput/RevealableInput';
import { useStudentSignin } from './useAuth';
import './ChildSigninDialog.css';

interface Props {
  isOpen: boolean;
  onClose: () => void;
}

/**
 * Sign-in dialog for kids in a teacher-managed classroom.
 *
 * Three fields, all kid-friendly:
 *   * 8-char class code (the teacher writes this on the board).
 *   * Display name (whatever the teacher entered on the roster).
 *   * 4-8 digit PIN.
 *
 * Auto-uppercases the class code as the kid types — the teacher's
 * sign is in uppercase. We strip non-alphanumerics on submit.
 *
 * Reuses the `bl-childsignin__*` styles from ChildSigninDialog —
 * the kid-side dialogs share a visual language. Separate component
 * because the field set is different (no parent email).
 */
export function ClassSigninDialog({ isOpen, onClose }: Props): JSX.Element | null {
  const titleId = useId();
  const [classCode, setClassCode] = useState('');
  const [displayName, setDisplayName] = useState('');
  const [pin, setPin] = useState('');
  const signin = useStudentSignin();

  useEffect(() => {
    if (!isOpen) {
      setClassCode('');
      setDisplayName('');
      setPin('');
      signin.reset();
      return;
    }
    const handler = (e: KeyboardEvent): void => {
      if (e.key === 'Escape' && !signin.isPending) onClose();
    };
    window.addEventListener('keydown', handler);
    return () => window.removeEventListener('keydown', handler);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen]);

  if (!isOpen) return null;

  const cleanedCode = classCode.replace(/\s+/g, '').toUpperCase();
  const trimmedName = displayName.trim();
  const pinValid = pin.length >= 4 && pin.length <= 8 && /^\d+$/.test(pin);
  const codeValid = /^[A-Z0-9]{8}$/.test(cleanedCode);
  const canSubmit = codeValid && trimmedName.length > 0 && pinValid && !signin.isPending;

  const handleSubmit = (e: React.FormEvent): void => {
    e.preventDefault();
    if (!canSubmit) return;
    signin.mutate(
      {
        classCode: cleanedCode,
        displayName: trimmedName,
        pin,
      },
      { onSuccess: () => onClose() },
    );
  };

  return (
    <div
      className="bl-childsignin__backdrop"
      role="presentation"
      onClick={(e) => {
        if (e.target === e.currentTarget && !signin.isPending) onClose();
      }}
    >
      <div
        className="bl-childsignin__dialog"
        role="dialog"
        aria-modal="true"
        aria-labelledby={titleId}
      >
        <h2 id={titleId} className="bl-childsignin__title">
          Sign in with your class code
        </h2>
        <p className="bl-childsignin__lead">
          Your teacher will tell you the code, your name, and your PIN.
        </p>

        <form className="bl-childsignin__form" onSubmit={handleSubmit}>
          <label className="bl-childsignin__field">
            <span>Class code</span>
            <input
              type="text"
              autoComplete="off"
              autoCapitalize="characters"
              spellCheck={false}
              placeholder="e.g. PURPLE12"
              value={classCode}
              onChange={(e) => setClassCode(e.target.value.toUpperCase())}
              maxLength={12}
              required
            />
          </label>
          <label className="bl-childsignin__field">
            <span>Your name</span>
            <input
              type="text"
              autoComplete="username"
              value={displayName}
              onChange={(e) => setDisplayName(e.target.value)}
              maxLength={80}
              required
            />
          </label>
          {/* eslint-disable-next-line jsx-a11y/label-has-associated-control -- RevealableInput renders a real input; lint rule doesn't trace through the custom wrapper */}
          <label className="bl-childsignin__field">
            <span>PIN</span>
            <RevealableInput
              defaultType="tel"
              inputMode="numeric"
              autoComplete="off"
              pattern="[0-9]*"
              value={pin}
              onChange={(e) => setPin(e.target.value.replace(/\D/g, ''))}
              maxLength={8}
              required
            />
          </label>

          {signin.error && (
            <p className="bl-childsignin__error" role="alert">
              {signin.error.message}
            </p>
          )}

          <div className="bl-childsignin__actions">
            <Button type="button" variant="secondary" onClick={onClose} disabled={signin.isPending}>
              Cancel
            </Button>
            <Button type="submit" disabled={!canSubmit}>
              {signin.isPending ? 'Signing in…' : 'Sign in'}
            </Button>
          </div>
        </form>
      </div>
    </div>
  );
}
